import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { api } from "../lib/api";
import Reveal from "../components/Reveal";

const KIND_LABELS = {
    general: "Informazioni generali",
    format: "Proponi un format",
    event: "Richiedi un evento",
    partner: "Diventa partner",
    casting: "Partecipa ai casting",
};

export default function ContactPage() {
    const [params] = useSearchParams();
    const initialKind = params.get("kind") || "general";
    const [form, setForm] = useState({
        name: "",
        email: "",
        subject: "",
        message: "",
        kind: initialKind,
    });
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/contact", form);
            setSent(true);
            setForm({ name: "", email: "", subject: "", message: "", kind: "general" });
            toast.success("Richiesta inviata. Ti risponderemo a breve.");
        } catch (err) {
            toast.error("Errore nell'invio. Riprova.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <Toaster position="top-center" richColors />
            <section className="pt-36 md:pt-44 pb-16 container-nm">
                <Reveal>
                    <div className="kicker mb-6">Contatti</div>
                </Reveal>
                <Reveal delay={100}>
                    <h1 className="font-display text-[2.5rem] sm:text-5xl md:text-6xl lg:text-[6rem] font-extrabold leading-[0.95] tracking-[-0.035em]">
                        Parla con noi.
                    </h1>
                </Reveal>
                <Reveal delay={200}>
                    <p className="mt-6 text-lg md:text-xl text-white/75 max-w-2xl">
                        Contattaci per progetti, collaborazioni o informazioni. Il team ti risponderà entro
                        48h.
                    </p>
                </Reveal>
            </section>

            <section className="container-nm pb-32">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                    <Reveal className="md:col-span-4">
                        <div className="border-t border-white/10 divide-y divide-white/10">
                            <div className="py-5">
                                <div className="kicker text-white/50 mb-2">Email</div>
                                <a href="mailto:segreteria@nemmanagement.it" className="text-lg hover:text-[color:var(--brand-accent)]">
                                    segreteria@nemmanagement.it
                                </a>
                            </div>
                            <div className="py-5">
                                <div className="kicker text-white/50 mb-2">Sede legale</div>
                                <div className="text-lg leading-snug">
                                    Galleria Cosimo Fanzago, 3<br />
                                    24121 Bergamo
                                </div>
                            </div>
                            <div className="py-5">
                                <div className="kicker text-white/50 mb-2">Sede operativa</div>
                                <div className="text-lg leading-snug">
                                    Via T. Tasso, 35<br />
                                    24121 Bergamo
                                </div>
                            </div>
                            <div className="py-5">
                                <div className="kicker text-white/50 mb-2">P. IVA</div>
                                <div className="text-lg">09224470964</div>
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={150} className="md:col-span-8">
                        {sent ? (
                            <div className="border border-[color:var(--brand-blue)] p-10" data-testid="contact-success">
                                <h3 className="font-display text-3xl font-bold mb-3">Grazie!</h3>
                                <p className="text-white/70">La tua richiesta è stata ricevuta. Ti risponderemo il prima possibile.</p>
                                <button className="btn-ghost text-white mt-8" onClick={() => setSent(false)}>
                                    Invia un'altra richiesta
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                                <div>
                                    <label className="kicker text-white/50 block mb-3">Tipo richiesta</label>
                                    <div className="flex flex-wrap gap-2">
                                        {Object.entries(KIND_LABELS).map(([k, label]) => (
                                            <button
                                                type="button"
                                                key={k}
                                                onClick={() => setForm((f) => ({ ...f, kind: k }))}
                                                className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border transition-all duration-300 ${
                                                    form.kind === k
                                                        ? "bg-[color:var(--brand-blue)] text-white border-[color:var(--brand-blue)]"
                                                        : "border-white/20 text-white/70 hover:text-white hover:border-white"
                                                }`}
                                                data-testid={`contact-kind-${k}`}
                                            >
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <Field label="Nome e cognome" required>
                                        <input
                                            required
                                            type="text"
                                            value={form.name}
                                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                                            data-testid="contact-name"
                                            className="bg-transparent border-0 border-b border-white/30 focus:border-[color:var(--brand-blue)] outline-none w-full py-3 text-lg"
                                        />
                                    </Field>
                                    <Field label="Email" required>
                                        <input
                                            required
                                            type="email"
                                            value={form.email}
                                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                                            data-testid="contact-email"
                                            className="bg-transparent border-0 border-b border-white/30 focus:border-[color:var(--brand-blue)] outline-none w-full py-3 text-lg"
                                        />
                                    </Field>
                                </div>
                                <Field label="Oggetto">
                                    <input
                                        type="text"
                                        value={form.subject}
                                        onChange={(e) => setForm({ ...form, subject: e.target.value })}
                                        data-testid="contact-subject"
                                        className="bg-transparent border-0 border-b border-white/30 focus:border-[color:var(--brand-blue)] outline-none w-full py-3 text-lg"
                                    />
                                </Field>
                                <Field label="Messaggio" required>
                                    <textarea
                                        required
                                        rows={6}
                                        value={form.message}
                                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                                        data-testid="contact-message"
                                        className="bg-transparent border-0 border-b border-white/30 focus:border-[color:var(--brand-blue)] outline-none w-full py-3 text-lg resize-none"
                                    />
                                </Field>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="btn-primary disabled:opacity-50"
                                    data-testid="contact-submit"
                                >
                                    {loading ? "Invio…" : "Invia richiesta"}
                                </button>
                            </form>
                        )}
                    </Reveal>
                </div>
            </section>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="kicker text-white/50 mb-2 block">
                {label}
                {required && <span className="text-[color:var(--brand-blue)]"> *</span>}
            </span>
            {children}
        </label>
    );
}
